import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import MarketingHero from './MarketingHero';
import MarketingSection from './MarketingSection';
import FadeIn from './FadeIn';
import { EASE_REFINED } from './constants';

function LegalSectionBlock({ section }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.section
      ref={ref}
      id={section.id}
      className="legal-doc-section"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE_REFINED }}
    >
      <h2 className="landing-heading legal-doc-section__title">{section.title}</h2>
      {section.content.map((para, i) => (
        <p key={i} className="marketing-prose__p">
          {para}
        </p>
      ))}
    </motion.section>
  );
}

function LegalTocNav({ sections, activeSection, onSelect, className = '' }) {
  return (
    <nav className={`legal-doc-toc ${className}`.trim()} aria-label="Table of contents">
      <div className="legal-doc-toc__label">Contents</div>
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() => onSelect(section.id)}
          className={`legal-doc-toc__link${activeSection === section.id ? ' legal-doc-toc__link--active' : ''}`}
          aria-current={activeSection === section.id ? 'location' : undefined}
        >
          {section.title}
        </button>
      ))}
    </nav>
  );
}

export default function LegalDocLayout({
  title,
  lastUpdated,
  sections,
  crossLinkTo,
  crossLinkLabel,
}) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id);

  useEffect(() => {
    function handleScroll() {
      for (const section of [...sections].reverse()) {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 160) {
            setActiveSection(section.id);
            return;
          }
        }
      }
      if (sections[0]) setActiveSection(sections[0].id);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="legal-doc-layout">
      <MarketingHero
        eyebrow="Legal"
        title={title}
        meta={`Last updated: ${lastUpdated}`}
        compact
      />

      <MarketingSection padding="sm" maxWidth="5xl">
        <details className="legal-doc-toc-mobile lg:hidden">
          <summary className="legal-doc-toc-mobile__summary">Jump to section</summary>
          <LegalTocNav
            sections={sections}
            activeSection={activeSection}
            onSelect={scrollTo}
            className="legal-doc-toc--mobile"
          />
        </details>

        <div className="legal-doc-layout__grid">
          <div className="hidden lg:block">
            <div className="legal-doc-toc-sticky">
              <LegalTocNav
                sections={sections}
                activeSection={activeSection}
                onSelect={scrollTo}
              />
            </div>
          </div>

          <article className="marketing-prose legal-doc-layout__content">
            {sections.map((section) => (
              <LegalSectionBlock key={section.id} section={section} />
            ))}

            {crossLinkTo && crossLinkLabel && (
              <FadeIn delay={0.1}>
                <div className="legal-doc-crosslink">
                  <span className="legal-doc-crosslink__label">Related</span>
                  <Link to={crossLinkTo} className="legal-doc-crosslink__link">
                    {crossLinkLabel}
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path
                        d="M2 6h8M7 3l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </div>
              </FadeIn>
            )}
          </article>
        </div>
      </MarketingSection>
    </div>
  );
}
